import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '@/components/AuthProvider';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { TrendingUp, Users, Calendar, BarChart3, Truck, UserCircle, DollarSign, Eye, BarChart, Settings, ArrowLeft, Leaf, Bell, HelpCircle, MessageSquare, FileText, Activity } from "lucide-react";
import { useCSATSettings } from '@/hooks/useCSATSettings';
import { useCSATMetrics } from '@/hooks/useCSATMetrics';
import { CSATPopup } from '@/components/csat/CSATPopup';
import { RatingDistributionChart } from '@/components/csat/RatingDistributionChart';
import { TemporalEvolutionChart } from '@/components/csat/TemporalEvolutionChart';
import { CSATByPageTable } from '@/components/csat/CSATByPageTable';
import { NegativeCommentsCard } from '@/components/csat/NegativeCommentsCard';
import { WordFrequencyChart } from '@/components/csat/WordFrequencyChart';

const CSAT = () => {
  const { profile, isLoading } = useAuthContext();
  const navigate = useNavigate();
  const { settings, isLoading: isLoadingSettings, updateSetting, updateAllSettings } = useCSATSettings();
  
  const [displayDays, setDisplayDays] = useState(5);
  const [metricsRange, setMetricsRange] = useState(30);
  const [showPreview, setShowPreview] = useState(false);
  
  const { data: metrics, isLoading: isLoadingMetrics } = useCSATMetrics(metricsRange);

  // Atualizar displayDays quando as configurações carregarem
  useEffect(() => {
    if (settings && settings.length > 0) {
      // Pegar o display_days da primeira configuração (assumindo que seja o mesmo para todas)
      setDisplayDays(settings[0].display_days);
    }
  }, [settings]);

  const handleToggle = (pageId: string, currentState: boolean) => {
    updateSetting({ 
      pageId, 
      isEnabled: !currentState 
    });
  };

  const handleDisplayDaysChange = (value: number) => {
    setDisplayDays(value);
    // Atualizar para todas as páginas em lote
    if (settings && settings.length > 0) {
      updateAllSettings({ 
        pageIds: settings.map(s => s.page_id),
        displayDays: value 
      });
    }
  };

  const handleToggleAll = (enable: boolean) => {
    if (settings && settings.length > 0) {
      updateAllSettings({ 
        pageIds: settings.map(s => s.page_id),
        isEnabled: enable 
      });
    }
  };

  // Verificar se todas as páginas estão ativas
  const allPagesEnabled = settings?.every(setting => setting.is_enabled) || false;

  const pages = [
    { id: 'dashboard', name: 'Dashboard', description: 'Página principal', icon: BarChart3, path: '/' },
    { id: 'coletasRecorrentes', name: 'Coletas Recorrentes', description: 'Gestão de coletas recorrentes', icon: Truck, path: '/coletasrecorrentes' },
    { id: 'coletasSobDemanda', name: 'Coletas Sob Demanda', description: 'Gestão de coletas sob demanda', icon: Truck, path: '/coletasobdemanda' },
    { id: 'usersPermissions', name: 'Usuários e Permissões', description: 'Gestão de usuários', icon: UserCircle, path: '/users-permissions' },
    { id: 'financeiro', name: 'Financeiro', description: 'Gestão financeira', icon: DollarSign, path: '/financeiro' },
    // Novas páginas
    { id: 'dadosImpacto', name: 'Dados de Impacto', description: 'Métricas de impacto ambiental', icon: Leaf, path: '/dadosimpacto' },
    { id: 'configuracoes', name: 'Configurações', description: 'Configurações do portal', icon: Settings, path: '/configuracoes' },
    // Mitigação temporária: Notificações oculto até correção definitiva
    // { id: 'notificacoes', name: 'Notificações', description: 'Preferências de notificação', icon: Bell, path: '/notificacoes' },
    { id: 'perguntasFrequentes', name: 'Perguntas Frequentes', description: 'FAQ do portal', icon: HelpCircle, path: '/perguntasfrequentes' },
    { id: 'suporte', name: 'Suporte', description: 'Central de ajuda', icon: MessageSquare, path: '/suporte' },
    { id: 'termosDeUso', name: 'Termos de Uso', description: 'Termos e condições', icon: FileText, path: '/termosdeuso' },
    { id: 'statusMetrics', name: 'Status Metrics', description: 'Métricas de status (interno)', icon: Activity, path: '/status-metrics' },
    { id: 'coletaDetalhes', name: 'Detalhes da Coleta', description: 'Página de detalhes de coleta', icon: Eye, path: '/coletasobdemanda/:id' },
  ];

  useEffect(() => {
    if (!isLoading && (!profile || !profile.is_internal)) {
      navigate('/unauthorized');
    }
  }, [profile, isLoading, navigate]);

  if (isLoading || isLoadingSettings || isLoadingMetrics) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!profile?.is_internal) {
    return null;
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div>
        <div className="flex items-center gap-3">
          <button 
            onClick={() => navigate('/configuracoes')}
            className="p-1 rounded-md hover:bg-accent transition-colors"
          >
            <ArrowLeft className="h-6 w-6 text-foreground" />
          </button>
          <h1 className="text-3xl font-bold text-foreground">CSAT - Customer Satisfaction</h1>
        </div>
        <p className="text-muted-foreground mt-2">
          Análise de satisfação e feedback dos clientes
        </p>
      </div>

      <Tabs defaultValue="settings" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="settings" className="gap-2">
            <Settings className="h-4 w-4" />
            Configurações
          </TabsTrigger>
          <TabsTrigger value="metrics" className="gap-2">
            <BarChart className="h-4 w-4" />
            Métricas
          </TabsTrigger>
        </TabsList>

        <TabsContent value="settings" className="space-y-6 mt-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Configuração de CSAT</CardTitle>
                  <CardDescription className="mt-2">
                    Ative ou desative a pesquisa de satisfação para cada página do portal
                  </CardDescription>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowPreview(true)}
                  className="gap-2"
                >
                  <Eye className="h-4 w-4" />
                  Visualizar Popup
                </Button>
              </div>
              
              <div className="flex items-center gap-4 pt-4 mt-4 border-t">
                <div className="flex items-center gap-2">
                  <Label htmlFor="enable-all" className="text-sm whitespace-nowrap">
                    Ativar todas:
                  </Label>
                  <Switch
                    id="enable-all"
                    checked={allPagesEnabled}
                    onCheckedChange={handleToggleAll}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Label htmlFor="display-time" className="text-sm whitespace-nowrap">
                    Exibir a cada (dias):
                  </Label>
                  <input
                    id="display-time"
                    type="number"
                    min="1"
                    value={displayDays}
                    onChange={(e) => {
                      const value = e.target.value === '' ? 1 : parseInt(e.target.value);
                      handleDisplayDaysChange(value);
                    }}
                    onBlur={(e) => {
                      if (!e.target.value || parseInt(e.target.value) < 1) {
                        handleDisplayDaysChange(1);
                      }
                    }}
                    className="w-20 px-2 py-1 text-sm border rounded-md bg-background"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {settings?.map((setting) => {
                  const page = pages.find(p => p.id === setting.page_id);
                  if (!page) return null;
                  
                  const Icon = page.icon;
                  return (
                    <div 
                      key={setting.page_id}
                      className="flex items-center justify-between p-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <Icon className="h-5 w-5 text-muted-foreground" />
                        <div>
                          <Label 
                            htmlFor={`toggle-${setting.page_id}`}
                            className="text-sm font-medium cursor-pointer"
                          >
                            {setting.page_name}
                          </Label>
                          <p className="text-xs text-muted-foreground">
                            {page.description} • {setting.page_path}
                          </p>
                        </div>
                      </div>
                      <Switch
                        id={`toggle-${setting.page_id}`}
                        checked={setting.is_enabled}
                        onCheckedChange={() => handleToggle(setting.page_id, setting.is_enabled)}
                      />
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="metrics" className="space-y-6 mt-6">
          {/* Seletor de Período */}
          <div className="flex items-center justify-end">
            <div className="flex items-center gap-2">
              <Label htmlFor="metrics-range" className="text-sm text-muted-foreground whitespace-nowrap">
                Período:
              </Label>
              <Select
                value={metricsRange.toString()}
                onValueChange={(value) => setMetricsRange(parseInt(value))}
              >
                <SelectTrigger id="metrics-range" className="w-[160px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">Últimos 7 dias</SelectItem>
                  <SelectItem value="30">Últimos 30 dias</SelectItem>
                  <SelectItem value="90">Últimos 90 dias</SelectItem>
                  <SelectItem value="180">Últimos 6 meses</SelectItem>
                  <SelectItem value="365">Último ano</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Cards de Métricas Principais */}
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  CSAT Médio
                </CardTitle>
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {metrics?.totalResponses === 0 ? (
                    <span className="text-muted-foreground text-base">Sem dados</span>
                  ) : (
                    `${metrics?.csatScore}%`
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  {metrics?.totalResponses === 0 
                    ? 'Nenhuma resposta coletada' 
                    : `${metrics?.satisfiedResponses} de ${metrics?.totalResponses} respostas satisfeitas (≥4)`
                  }
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  Feedbacks Recebidos
                </CardTitle>
                <Users className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{metrics?.totalResponses || 0}</div>
                <p className="text-xs text-muted-foreground">
                  {metricsRange === 7 ? 'Últimos 7 dias' : 
                   metricsRange === 30 ? 'Últimos 30 dias' : 
                   metricsRange === 90 ? 'Últimos 90 dias' : 
                   metricsRange === 180 ? 'Últimos 6 meses' : 'Último ano'}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  Engajamento de Feedback
                </CardTitle>
                <Calendar className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {metrics?.totalResponses === 0 ? (
                    <span className="text-muted-foreground text-base">-</span>
                  ) : (
                    `${metrics?.averageRating}/5.0`
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  {metrics?.totalResponses === 0 
                    ? 'Nenhuma avaliação' 
                    : 'Média de todas as avaliações'
                  }
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Seção de Análises Detalhadas */}
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-semibold text-foreground mb-2">Análises Detalhadas</h2>
              <p className="text-muted-foreground">
                Visualizações e métricas avançadas de satisfação do cliente
              </p>
            </div>

            {/* Distribuição de Notas e Evolução Temporal */}
            <div className="grid gap-6 lg:grid-cols-2">
              <RatingDistributionChart daysRange={metricsRange} />
              <TemporalEvolutionChart daysRange={metricsRange} />
            </div>

            {/* CSAT por Página */}
            <CSATByPageTable daysRange={metricsRange} />

            {/* Comentários Negativos e Temas Mais Citados */}
            <div className="grid gap-6 lg:grid-cols-2">
              <NegativeCommentsCard daysRange={metricsRange} />
              <WordFrequencyChart daysRange={metricsRange} />
            </div>
          </div>
        </TabsContent>
      </Tabs>

      <CSATPopup isOpen={showPreview} onClose={() => setShowPreview(false)} />
    </div>
  );
};

export default CSAT;
